import {
    Activity,
    CarFeatures,
    CompanyDocument,
    Event,
    Fuel,
    guestCategory,
    Industry,
    Itinerary,
    Priority,
    type SelectOptionType,
    Sharing,
    Ship,
    Stage,
    TimeInterval,
    Transmission,
    UserDocument,
    VehicleCategory,
    VehicleClass,
} from '~/utils/types/types'

export const useStages: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const stages: Stage[] = [Stage.Client, Stage.Potential]
    return stages.map(value => ({value, title: t(`crmStage.${value}`)}))
})

export const usePriorities: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const priorities: Priority[] = [Priority.VIP, Priority.High, Priority.Normal, Priority.Low]
    return priorities.map(value => ({value, title: t(`crmPriority.${value}`)}))
})

export const useIndustries: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const industries: Industry[] = [Industry.Finance, Industry.FoodAndBeverage, Industry.Insurance, Industry.Software]
    return industries.map(value => ({value, title: t(`industry_type.${value}`)}))
})

export const useCompanySize: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const companySizes = [
        'SIZE_1_10',
        'SIZE_11_49',
        'SIZE_50_99',
        'SIZE_100_299',
        'SIZE_300_999',
        'SIZE_MORE_THAN_1000',
    ]
    return companySizes.map(value => ({value, title: t(`company_sizes.${value}`)}))
})

export const useCompanyDocument: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const documents: CompanyDocument[] = [
        CompanyDocument.Contract,
        CompanyDocument.GuaranteeLetter,
        CompanyDocument.InfoLetter,
        CompanyDocument.LetterOfAttorney,
        CompanyDocument.Logo,
        CompanyDocument.Offer,
        CompanyDocument.Photo,
        CompanyDocument.PriceList,
        CompanyDocument.Questionary,
        CompanyDocument.Report,
        CompanyDocument.Request,
        CompanyDocument.TripPolicy,
        CompanyDocument.Other,
    ]
    return documents.map(value => ({value, title: t(`documentTypes.${value}`)}))
})

export const useUserDocument: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const documents: UserDocument[] = [
        UserDocument.Passport,
        UserDocument.BirthCertificate,
        UserDocument.Questionary,
        UserDocument.LetterOfAttorney,
        UserDocument.Sof,
        UserDocument.Visa,
        UserDocument.Photo,
        UserDocument.MedicalCertificate,
    ]
    return documents.map(value => ({value, title: t(`documentTypes.${value}`)}))
})

export const useCountries: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    return runtimeStore.countries.map(({id, name}) => ({title: name, value: id}))
})

export const useHotelInfo = computed(() => {
    return supplierStore.hotelInfo
})

export const useCategoryDictionary = computed(() => {
    return supplierStore.categoryDictionary
})

export const useIsLoadingHotelInfo = computed(() => {
    return supplierStore.isLoading
})

export const useFullContract = computed(() => {
    return supplierStore.fullContract
})

export const usePaxes = computed(() => {
    return supplierStore.paxes.sort((a, b) => b.isDefault - a.isDefault)
})

export const useCatalogue = computed(() => {
    return supplierStore.catalogue
})

export const useProductServices = computed(() => {
    return supplierStore.productServices
})

export const useCharterServiceCategories = computed(() => {
    return supplierStore.charterServiceCategories
})

export const useOffices = computed(() => {
    return supplierStore.offices
})

export const useGuestCategory: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const guestCategories: guestCategory[] = [guestCategory.Adults, guestCategory.Children]
    return guestCategories.map(value => ({value, title: t(`guestCategory.${value}`)}))
})

export const useAirlines: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const app = useNuxtApp()
    return flightsStore.airlines
        .map(({codes, name}) => ({
            title: name[app.$i18n.locale.value] || name['en'],
            value: codes.IATA,
        }))
        .sort((a, b) => a.title.localeCompare(b.title))
})

export const useActivityCategory: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const activityCategories: Activity[] = [Activity.Excursion, Activity.Event]
    return activityCategories.map(value => ({value, title: t(`serviceType.${value}`)}))
})

export const useSharing: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const sharingTypes: Sharing[] = [Sharing.Shared, Sharing.Private]
    return sharingTypes.map(value => ({value, title: t(`sharingType.${value}`)}))
})

export const useTimeInterval: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const timeIntervals: TimeInterval[] = [TimeInterval.AnyTime, TimeInterval.FixedTime, TimeInterval.Interval]
    return timeIntervals.map(value => ({value, title: t(`timeIntervalType.${value}`)}))
})

export const useFirstColumnFeatures: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const features: CarFeatures[] = [
        CarFeatures.AirConditioning,
        CarFeatures.AutomaticTransmission,
        CarFeatures.ManualTransmission,
        CarFeatures.BackUpCamera,
        CarFeatures.BlindSpotMonitoring,
    ]
    return features.map(value => ({value, title: t(`filters_features.${value}`)}))
})

export const useSecondColumnFeatures: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const features: CarFeatures[] = [
        CarFeatures.Bluetooth,
        CarFeatures.KeylessEntry,
        CarFeatures.RoadsideAssistance,
        CarFeatures.ThirdPartyInsurance,
        CarFeatures.UnlimitedMileage,
    ]
    return features.map(value => ({value, title: t(`filters_features.${value}`)}))
})

export const useFuel: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const fuel: Fuel[] = [
        Fuel.Diesel,
        Fuel.Hybrid,
        Fuel.Electric,
        Fuel.Gas,
        Fuel.Hydrogen,
        Fuel.MultiFuel,
        Fuel.Petrol,
        Fuel.Ethanol,
    ]
    return fuel.map(value => ({value, title: t(`carsRent.fuelType.${value}`)}))
})

export const useVehicleClass: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const vehicleClass: VehicleClass[] = [
        VehicleClass.Compact,
        VehicleClass.Economy,
        VehicleClass.Electric,
        VehicleClass.FullSize,
        VehicleClass.Luxury,
        VehicleClass.MidSize,
        VehicleClass.Premium,
        VehicleClass.Standard,
    ]
    return vehicleClass.map(value => ({value, title: t(`carsRent.vehicleClass.${value}`)}))
})

export const useTransmission: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const transmission: Transmission[] = [Transmission.Automatic, Transmission.Manual]
    return transmission.map(value => ({value, title: t(`carsRent.transmissionType.${value}`)}))
})

export const useVehicleCategory: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const category: VehicleCategory[] = [
        VehicleCategory.Car,
        VehicleCategory.Minivan,
        VehicleCategory.Pickup,
        VehicleCategory.Suv,
        VehicleCategory.Truck,
        VehicleCategory.Van,
    ]
    return category.map(value => ({value, title: t(`carsRent.vehicleCategory.${value}`)}))
})

export const useItinerary: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const itinerary: Itinerary[] = [
        Itinerary.Accommodation,
        Itinerary.Transfer,
        Itinerary.Flight,
        Itinerary.Excursion,
        Itinerary.Insurance,
        Itinerary.Event,
        Itinerary.Extra,
        Itinerary.Car,
        Itinerary.Cruise,
    ]
    return itinerary.map(value => ({value, title: t(`itineraryType.${value}`)}))
})

export const useShip: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const ship: Ship[] = [Ship.Gulet, Ship.Catamaran, Ship.CruiseShip, Ship.MotorYacht, Ship.SailingYacht]
    return ship.map(value => ({value, title: t(`cruises.ship_types.${value}`)}))
})

export const useCurrency = computed(() => {
    const app = useNuxtApp()
    return Object.values(app.$conf.public.currency.available).filter(currency =>
        Object.keys(runtimeStore.exchangeRates).includes(currency)
    )
})

export const useEvent: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const event: Event[] = [Event.Sport, Event.Concert, Event.Other]
    return event.map(value => ({value, title: t(`eventType.${value}`)}))
})

export const useContractStage: globalThis.ComputedRef<SelectOptionType[]> = computed(() => {
    const {t} = useI18n()
    const stage = ['DRAFT', 'APPROVAL', 'PUBLISHED']
    return stage.map(value => ({value, title: t(`stageType.${value}`)}))
})

export const useIsShowPublishing = computed(() => {
    return runtimeStore.getCRMOption('asp.option.show.product.publishing') && authStore.isTO1
})

export const useAllSelectedHeaders = computed(() => {
    const {t} = useI18n()
    return [{title: t('supplier.all'), value: 'name'}]
})

export const useDeviceTypeItems = computed(() => {
    const {t} = useI18n()
    return ['DESKTOP', 'MOBILE'].map(value => ({id: value, name: t(`device_type_item.${value}`)}))
})

export const useAllocationTypeItems = computed(() => {
    const {t} = useI18n()
    return ['Base', 'Extra', 'WithoutPlace'].map((value, index) => ({id: index, name: t(`placeType.${value}`)}))
})
